import { readItemField } from './item-field-reader';
import { readItemUuidSlot } from './inventory-raw-readers';
import { plainTextDecode } from './possessor-compile';
import { fromBaseClone, fromBaseRecord, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';

export interface InventoryReferenceMetadata {
    layerCatalog: Readonly<Record<string, { references_item_type?: string; full_nest?: boolean; is_multiple?: boolean }>>;
    /** Raw frontend registry rows. Null type = no static registry; null id = a known static miss. */
    shellRegistries?: Readonly<Record<string, Readonly<Record<string, Row | null>> | null>>;
    /** Complete registries omit misses instead of repeating null for every UUID. */
    shellRegistriesComplete?: boolean;
    /** Raw items-table shell identity rows; explicit null means not an item. */
    shellRows?: Readonly<Record<string, Row | null>>;
    sizeCatalog: readonly Row[];
}
const shellItems = new Set(['item_id', 'item_name', 'item_type', 'item_description', 'flavor_text']);

export interface InventoryFullReferenceContext {
    /** A completed definition for this exact ancestor chain, never a raw source or cached standalone expansion. */
    fullDefinition: (id: string, ancestors: readonly string[]) => Row | null;
    ancestors?: readonly string[];
}

/** Native reference pass over canonical authored layers. Full definitions are
 * an explicit dependency; a missing stage must propagate from the constructor. */
export function constructInventoryDefinitionReferences(source: Row, metadata: InventoryReferenceMetadata,
    rawDefinition: (id: string) => Row | null, context?: InventoryFullReferenceContext): Row {
    const result = fromBaseClone(source), layers = { ...result.item_layer_types as Row };
    const identity = readItemField(source, 'item_id');
    const ancestors = [...new Set([...(context?.ancestors ?? []),
        ...(typeof identity === 'string' && identity !== '' ? [identity] : [])])];
    const shell = (id: string, type: string) => constructInventoryReferenceShell(id, type, metadata, rawDefinition);
    function expand(value: unknown, type: string, fullNest: boolean): unknown {
        return expandInventoryReference(value, type, fullNest, ancestors, shell, context);
    }
    for (const [name, value] of Object.entries(layers)) {
        const spec = metadata.layerCatalog[name], type = spec?.references_item_type;
        if (!type || (!context && spec.full_nest && name !== 'size')) continue;
        const fullNest = Boolean(context && spec.full_nest && name !== 'size' && ancestors.length <= 3);
        if (spec.is_multiple && value !== null && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, '0')) {
            const entries = Object.entries(value).map(([key, entry]) => [key, expand(entry, type, fullNest)] as const);
            layers[name] = entries.every(([key], index) => key === String(index))
                ? entries.map(([, entry]) => entry) : Object.fromEntries(entries);
        } else if (!Array.isArray(value)) layers[name] = expand(value, type, fullNest);
    }
    result.item_layer_types = layers;
    return result;
}

/** The same static/SELECT shell for authored references and computed UUID slots. */
export function constructInventoryReferenceShell(id: string, type: string, metadata: InventoryReferenceMetadata,
    rawDefinition: (id: string) => Row | null): Row | null {
        let registry = metadata.shellRegistries?.[type];
        // Native size always uses its static shell, even if full_nest drifted.
        if (type === 'size_type') registry = Object.fromEntries(metadata.sizeCatalog.map(row => [String(readItemField(row, 'item_id')), row]));
        if (registry === undefined) throw new FromBaseStageMissing('definition-shell-registry', type);
        if (registry !== null && !metadata.shellRegistriesComplete && !Object.prototype.hasOwnProperty.call(registry, id))
            throw new FromBaseInputMissing('static-shell', `${type}:${id}`);
        const entry = registry?.[id];
        if (entry && ['item_id', 'item_name', 'item_type'].every(name => readItemField(entry, name) != null)) {
            const items: Row = {}, fields: Row = {};
            for (const [name, value] of Object.entries(entry)) {
                if (name === 'variable_name') continue;
                (shellItems.has(name) ? items : fields)[name] = value;
            }
            return Object.keys(fields).length ? { items, item_layer_types: fields } : { items };
        }
        const held = metadata.shellRows && Object.prototype.hasOwnProperty.call(metadata.shellRows, id);
        const identity = held ? metadata.shellRows![id] : undefined;
        const raw = held ? identity ? { items: identity } : null : rawDefinition(id);
        if (!raw) return null;
        const name = plainTextDecode(String(readItemField(raw, 'item_name') ?? ''));
        if (name === null) throw new FromBaseStageMissing('definition-shell-name', id);
        return { items: { item_id: String(readItemField(raw, 'item_id')), item_name: name,
            item_type: String(readItemField(raw, 'item_type') ?? '') } };
}

function expandInventoryReference(value: unknown, type: string, fullNest: boolean, ancestors: readonly string[],
    shell: (id: string, type: string) => Row | null, context?: InventoryFullReferenceContext): unknown {
        // This is a structural copier: scalar readers would erase the native
        // distinction between a wrapped slot, rich carrier and expanded shell.
        const nestedIdentity = fromBaseRecord(value) && fromBaseRecord(value.items)
            ? readItemField({ items: value.items }, 'item_id') : null;
        if (fromBaseRecord(value) && (nestedIdentity != null || value.item_name != null
            || value.item_layer_effect != null || value.qty != null || value.template_id != null)) return value;
        // Native tests the bare identity after declining a nested identity.
        // A present-but-null nested item_id must not hide the bare fallback.
        if (typeof value !== 'string' && (!fromBaseRecord(value)
            || typeof readItemField({ ...value, items: undefined }, 'item_id') !== 'string')) return value;
        const id = readItemUuidSlot(value);
        if (!id) return value;
        // Native trims a scalar UUID, but validates a bare item_id verbatim.
        // The general slot reader also accepts scoring suffixes on rich refs.
        if (fromBaseRecord(value) && readItemField({ ...value, items: undefined }, 'item_id') !== id) return value;
        if (fullNest && !ancestors.includes(id)) {
            const nested = context!.fullDefinition(id, ancestors);
            if (nested && Object.keys(nested).length) return fromBaseClone(nested);
        }
        return shell(id, type) ?? value;
}

/** Shell-only checkpoint; never treats an unfinished full definition as a shell. */
export function constructInventoryDefinitionShells(source: Row, metadata: InventoryReferenceMetadata,
    rawDefinition: (id: string) => Row | null): Row {
    return constructInventoryDefinitionReferences(source, metadata, rawDefinition);
}
